import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  Mail,
  Shield,
  Clock,
  MoreHorizontal
} from 'lucide-react';
import AppShell from '../../components/AppShell';
import DataTable from '../../components/DataTable';
import Guard, { ProtectedButton } from '../../components/Guard';
import useAdminStore from '../../store';

const UsersListPage = () => {
  const navigate = useNavigate();
  const {
    users,
    groups,
    loading,
    errors,
    fetchUsers,
    deleteUser,
    updateUser
  } = useAdminStore();

  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Table configuration
  const columns = [
    {
      key: 'displayName',
      header: 'Name',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
            <span className="text-sm font-medium text-blue-600">
              {value.split(' ').map(n => n[0]).join('')}
            </span>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">{value}</div>
            <div className="text-sm text-gray-500">{row.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      type: 'badge',
      badgeClasses: (value) => {
        const classes = {
          admin: 'bg-red-100 text-red-800',
          doctor: 'bg-blue-100 text-blue-800',
          nurse: 'bg-green-100 text-green-800',
          receptionist: 'bg-purple-100 text-purple-800',
          manager: 'bg-orange-100 text-orange-800'
        };
        return classes[value] || 'bg-gray-100 text-gray-800';
      }
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      type: 'badge',
      badgeClasses: (value) => ({
        active: 'bg-green-100 text-green-800',
        inactive: 'bg-gray-100 text-gray-800',
        suspended: 'bg-red-100 text-red-800'
      }[value] || 'bg-gray-100 text-gray-800')
    },
    {
      key: 'groups',
      header: 'Groups',
      render: (value) => {
        if (!value || value.length === 0) {
          return <span className="text-gray-400">No groups</span>;
        }
        
        const groupNames = value.map(groupId => {
          const group = groups.find(g => g.id === groupId);
          return group ? group.name : groupId;
        });

        if (groupNames.length <= 2) {
          return (
            <div className="space-x-1">
              {groupNames.map((name, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                >
                  {name}
                </span>
              ))}
            </div>
          );
        }

        return (
          <div className="space-x-1">
            {groupNames.slice(0, 2).map((name, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
              >
                {name}
              </span>
            ))}
            <span className="text-xs text-gray-500">
              +{groupNames.length - 2} more
            </span>
          </div>
        );
      }
    },
    {
      key: 'lastLogin',
      header: 'Last Login',
      sortable: true,
      render: (value) => {
        if (!value) return <span className="text-gray-400">Never</span>;
        const date = new Date(value);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) {
          return <span className="text-green-600">Today</span>;
        } else if (diffDays === 1) {
          return <span className="text-yellow-600">Yesterday</span>;
        } else if (diffDays < 7) {
          return <span className="text-gray-600">{diffDays} days ago</span>;
        } else {
          return <span className="text-gray-400">{date.toLocaleDateString()}</span>;
        }
      }
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      type: 'date'
    }
  ];

  // Actions
  const handleView = (user) => {
    navigate(`/admin-iam/users/${user.id}`);
  };

  const handleEdit = (user) => {
    navigate(`/admin-iam/users/${user.id}/edit`);
  };

  const handleDelete = async (user) => {
    if (window.confirm(`Are you sure you want to delete ${user.displayName}?`)) {
      try {
        await deleteUser(user.id);
      } catch (error) {
        alert(`Failed to delete user: ${error.message}`);
      }
    }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    try {
      await updateUser(user.id, { status: newStatus });
    } catch (error) {
      alert(`Failed to update user status: ${error.message}`);
    }
  };

  const handleSendPasswordReset = (user) => {
    // Mock password reset
    alert(`Password reset email sent to ${user.email}`);
  };

  const rowActions = [
    {
      icon: Eye,
      label: 'View Details',
      handler: handleView,
      className: 'text-blue-600 hover:text-blue-700'
    },
    {
      icon: Edit,
      label: 'Edit User',
      handler: handleEdit,
      className: 'text-green-600 hover:text-green-700',
      permission: 'users.write'
    },
    {
      icon: Mail,
      label: 'Send Password Reset',
      handler: handleSendPasswordReset,
      className: 'text-purple-600 hover:text-purple-700',
      permission: 'users.write'
    },
    {
      icon: Trash2,
      label: 'Delete User',
      handler: handleDelete,
      className: 'text-red-600 hover:text-red-700',
      permission: 'users.delete'
    }
  ];

  // Filter actions based on permissions
  const getAvailableActions = () => {
    return rowActions.filter(action => {
      if (!action.permission) return true;
      // This would check actual permissions in a real app
      return true; // For demo purposes
    });
  };

  const bulkActions = [
    {
      label: 'Activate Users',
      icon: UserCheck,
      handler: (selectedIndexes) => {
        console.log('Activating users:', selectedIndexes);
        // Bulk activate logic
      }
    },
    {
      label: 'Deactivate Users',
      icon: UserX,
      handler: (selectedIndexes) => {
        console.log('Deactivating users:', selectedIndexes);
        // Bulk deactivate logic
      }
    },
    {
      label: 'Send Password Reset',
      icon: Mail,
      handler: (selectedIndexes) => {
        console.log('Sending password reset to users:', selectedIndexes);
        // Bulk password reset logic
      }
    }
  ];

  return (
    <AppShell 
      title="Users" 
      subtitle="Manage user accounts and access"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Guard permission="users.read" showMessage={true}>
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Users</h1>
              <p className="text-gray-600 mt-1">
                Manage user accounts, roles, and permissions
              </p>
            </div>
            
            <ProtectedButton
              permission="users.write"
              onClick={() => navigate('/admin-iam/users/new')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              disabledTitle="You don't have permission to create users"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </ProtectedButton>
          </div>

          <DataTable
            data={users}
            columns={columns}
            loading={loading.users}
            error={errors.users}
            title="All Users"
            subtitle={`${users.length} total users`}
            searchable={true}
            filterable={true}
            exportable={true}
            refreshable={true}
            selectable={true}
            pagination={true}
            pageSize={10}
            onRefresh={() => fetchUsers()}
            onRowClick={handleView}
            actions={getAvailableActions()}
            bulkActions={bulkActions}
            className="shadow-lg"
          />
        </Guard>
      </div>
    </AppShell>
  );
};

export default UsersListPage;
